<script setup lang="ts">
/**
 * A sidebar card component that displays a hierarchical list of checkboxes with collapsible sections.
 *
 * Features:
 * - Supports both flat and hierarchical (parent-child) item structures
 * - Collapsible list with configurable number of always-visible items
 * - Parent-child relationship handling:
 *   - Children become visible when parent is selected
 *   - Parent value is used for search when no children are selected
 *   - When any child is selected, only the child values are used for search
 *   - Deselecting a parent deselects all its children and hides them
 *   - Parent items with children auto-expand the list when selected
 * - Automatic expansion when hidden items are selected
 *
 * Props:
 * @prop {string} title - The title displayed at the top of the card
 * @prop {Item[]} items - Array of items to display. Each item can have children
 * @prop {any} modelValue - v-model value for selected items
 * @prop {boolean} [expandable=true] - Whether the list can be collapsed/expanded
 *
 * Item Structure:
 * Each item should have either:
 * - A string value (item itself is the string)
 * - An 'id' property
 * - A 'value' property
 * - A 'name' property for display
 * - Optional 'children' array with nested items
 */

// For items, a `name` prop is required. The `value` prop is optional.
// If there's no value set, the array index will be used as value.

type Item = (NamedValueItem | NamedApiEntity | ApiEntity | string) & {
  id?: id
  children?: Item[]
}

const props = withDefaults(defineProps<{
  title: string
  items: Item[]
  modelValue?: any
  expandable?: boolean
  itemCounts?: Record<id | string, number>
}>(), {
  modelValue: [],
  expandable: true,
  itemCounts: undefined,
})

const emits = defineEmits<{
  (e: 'update:modelValue', value: string | number | boolean): void
}>()

const value = useVModel(props, 'modelValue', emits, {
  passive: false,
})

// If the total list of items is longer than this number, the list will be collapsible
const numberOfItemsAlwaysVisible = props.expandable ? 4 : 1000

const isExpanded = ref(props.expandable ? false : true)
const selectedParents = ref<Set<string | number | boolean>>(new Set())

// Get the value to use for the checkbox, based on the type of item
function getValueForItem(item: Item, index: number): string | number | boolean | undefined {
  if (!item) return undefined
  if (typeof item === 'string') return item
  if ('id' in item) return String(item.id) // convert to string to avoid type errors
  if ('value' in item) return item.value
  return index
}

function getItemLabel(item: Item) {
  if (!item) return undefined
  if (typeof item === 'string') return item
  // Use non-breaking space characters (\u00A0) to add a bit more space between the emoji and the name
  if ('name' in item && 'emoji' in item && item.emoji) return `${item.emoji}\u00A0\u00A0${item.name}`
  if ('name' in item) return item.name
  return item?.id?.toString()
}

// When one of the hidden items is selected, expand the list
watchEffect(() => {
  if (!props.expandable) return

  // Check if any hidden items are selected
  const hiddenItems = props.items.slice(numberOfItemsAlwaysVisible)
  const hasSelectedHiddenItem = hiddenItems.some((item, index) =>
    value.value?.includes(getValueForItem(item, index + numberOfItemsAlwaysVisible)),
  )

  // Check if any parent items with children are selected
  const hasSelectedParentItem = props.items.some(item =>
    item.children?.length && value.value?.includes(getValueForItem(item, props.items.indexOf(item))),
  )

  // Expand the list if there are selected hidden items or if a parent with children is selected
  if (hasSelectedHiddenItem || hasSelectedParentItem) {
    isExpanded.value = true
  }
})

// Check if a specific parent item is selected
function isParentSelected(item: Item, index: number) {
  const parentValue = getValueForItem(item, index)
  return parentValue !== undefined && selectedParents.value.has(parentValue)
}

// Get all child values for a given parent item
function getChildValues(item: Item): (string | number | boolean)[] {
  if (!item.children?.length) return []
  return item.children.map((child, index) => getValueForItem(child, index)).filter(Boolean) as (string | number | boolean)[]
}

// Get parent value for an item
function getParentValue(item: Item, index: number): string | number | boolean | undefined {
  return getValueForItem(item, index)
}

// Check if any children of a parent are selected
function hasSelectedChildren(item: Item): boolean {
  const childValues = getChildValues(item)
  return childValues.some(childValue => value.value?.includes(childValue))
}

// Check if all children of a parent are selected
function areAllChildrenSelected(item: Item): boolean {
  const childValues = getChildValues(item)
  return childValues.length > 0 && childValues.every(childValue => value.value?.includes(childValue))
}

// Handle checkbox changes for parent items
function handleParentChange(checked: boolean, item: Item, index: number) {
  const parentValue = getValueForItem(item, index)
  if (parentValue === undefined) return

  if (!Array.isArray(value.value)) {
    value.value = []
  }

  if (checked) {
    // Track parent selection state
    selectedParents.value.add(parentValue)
    // When selecting parent, only add parent value if no children are selected
    if (!hasSelectedChildren(item)) {
      value.value = [...new Set([...value.value, parentValue])]
    }
  }
  else {
    // Remove parent from selected state
    selectedParents.value.delete(parentValue)
    // When deselecting parent, remove parent and all children
    const childValues = getChildValues(item)
    value.value = value.value.filter((val: string | number | boolean) =>
      val !== parentValue && !childValues.includes(val),
    )
  }
}

// Watch for changes in selected values to handle parent-child relationships
watch(value, (newValue) => {
  if (!Array.isArray(newValue)) return

  // Create a new array to hold the filtered values
  const filteredValues = [...newValue]

  // For each parent item
  props.items.forEach((item, parentIndex) => {
    if (!item.children?.length) return

    const parentValue = getParentValue(item, parentIndex)
    const hasChildren = hasSelectedChildren(item)
    const parentValueIndex = filteredValues.indexOf(parentValue)

    // Only remove parent value if there are selected children
    // Keep parent value if it exists and no children are selected
    if (hasChildren && parentValueIndex !== -1) {
      filteredValues.splice(parentValueIndex, 1)
    }
  })

  // Only update if values have changed to avoid infinite loop
  if (JSON.stringify(filteredValues) !== JSON.stringify(newValue)) {
    value.value = filteredValues
  }
})

// Check if a specific parent item is selected or has selected children
function shouldShowChildren(item: Item, index: number) {
  return isParentSelected(item, index) || hasSelectedChildren(item)
}

const showCounts = computed(() => props.itemCounts && Object.keys(props.itemCounts).length > 0)

function getItemCount(item: Item) {
  if (!props.itemCounts) return 0
  if ('id' in item && item.id) {
    // If it has an id, use that to get the count
    return props.itemCounts?.[item.id] || 0
  }
  // if it is a named value item, use the value
  if ('value' in item && item.value && typeof item.value === 'string') {
    return props.itemCounts?.[item.value] || 0
  }
  return 0
}

const checkboxClasses = {
  wrapper: 'text-theme-content',
  label: 'text-xs',
}

const countIndicatorClasses = ['text-primary-500 text-xs']
</script>

<template>
  <BaseCard v-if="props.items && props.items.length > 0" class="px-6 py-5">
    <div class="">
      <div class="space-y-10">
        <div>
          <VacancySearchSidebarHeading>{{ title }}</VacancySearchSidebarHeading>

          <ul class="space-y-4">
            <li
              v-for="(item, index) in props.items.slice(
                0,
                numberOfItemsAlwaysVisible,
              )"
              :key="item?.id ?? index"
              class="flex flex-col"
            >
              <div class="flex items-center justify-between">
                <BaseCheckbox
                  v-if="!item.children?.length"
                  v-model="value"
                  :value="getValueForItem(item, index)"
                  :label="getItemLabel(item)"
                  shape="rounded"
                  :classes="checkboxClasses"
                />
                <BaseCheckbox
                  v-else
                  :model-value="areAllChildrenSelected(item)"
                  :label="getItemLabel(item)"
                  shape="rounded"
                  :classes="checkboxClasses"
                  @update:model-value="checked => handleParentChange(checked, item, index)"
                />

                <span v-if="showCounts" :class="countIndicatorClasses">
                  {{ getItemCount(item) }}
                </span>
              </div>

              <!-- If item has children, display them in a nested list -->
              <Transition
                v-if="item.children?.length"
                enter-active-class="transition-all duration-500 ease-in"
                leave-active-class="transition-all duration-500 ease-out"
              >
                <ul v-if="shouldShowChildren(item, index)" class="ml-4 mt-4 space-y-3">
                  <li
                    v-for="(child, cIndex) in item.children"
                    :key="child.id ?? cIndex"
                  >
                    <div class="flex items-center justify-between">
                      <BaseCheckbox
                        v-model="value"
                        :value="getValueForItem(child, cIndex)"
                        :label="getItemLabel(child)"
                        shape="rounded"
                        :classes="checkboxClasses"
                      />

                      <span v-if="showCounts" :class="countIndicatorClasses">
                        {{ getItemCount(child) }}
                      </span>
                    </div>
                  </li>
                </ul>
              </Transition>
            </li>
          </ul>

          <Transition
            enter-active-class="transition-all duration-500 ease-in"
            enter-from-class="max-h-0 opacity-0"
            enter-to-class="max-h-[1000px] opacity-100"
            leave-active-class="transition-all duration-500 ease-out"
            leave-from-class="max-h-[1000px] opacity-100"
            leave-to-class="max-h-0 opacity-0"
          >
            <ul v-if="isExpanded" class="mt-4 space-y-4 overflow-hidden">
              <li
                v-for="(item, index) in props.items.slice(numberOfItemsAlwaysVisible)"
                :key="item?.id ?? index"
                class="flex flex-col"
              >
                <div class="flex items-center justify-between">
                  <BaseCheckbox
                    v-if="!item.children?.length"
                    v-model="value"
                    :value="getValueForItem(item, index + numberOfItemsAlwaysVisible)"
                    :label="getItemLabel(item)"
                    shape="rounded"
                    :classes="checkboxClasses"
                  />
                  <BaseCheckbox
                    v-else
                    :model-value="areAllChildrenSelected(item)"
                    :label="getItemLabel(item)"
                    shape="rounded"
                    :classes="checkboxClasses"
                    @update:model-value="checked => handleParentChange(checked, item, index + numberOfItemsAlwaysVisible)"
                  />

                  <span v-if="showCounts" :class="countIndicatorClasses">
                    {{ getItemCount(item) }}
                  </span>
                </div>

                <!-- Show children for items in the expanded section as well -->
                <Transition
                  v-if="item.children?.length"
                  enter-active-class="transition-all duration-500 ease-in"
                  leave-active-class="transition-all duration-500 ease-out"
                >
                  <ul v-if="isExpanded && shouldShowChildren(item, index + numberOfItemsAlwaysVisible)" class="ml-4 mt-4 space-y-3">
                    <li v-for="(child, cIndex) in item.children" :key="child.id ?? cIndex">
                      <div class="flex items-center justify-between">
                        <BaseCheckbox
                          v-model="value"
                          :value="getValueForItem(child, cIndex)"
                          :label="getItemLabel(child)"
                          shape="rounded"
                          :classes="checkboxClasses"
                        />

                        <span v-if="showCounts" :class="countIndicatorClasses">
                          {{ getItemCount(child) }}
                        </span>
                      </div>
                    </li>
                  </ul>
                </Transition>
              </li>
            </ul>
          </Transition>
          <button
            v-if="props.expandable && numberOfItemsAlwaysVisible < props.items.length"
            class="text-theme-content mt-4 inline-block cursor-pointer text-xs"
            @click="isExpanded = !isExpanded"
          >
            <Icon
              :name="
                isExpanded ? `mdi:chevron-up-circle` : `mdi:chevron-down-circle`
              "
              class="size-5"
            />
            <span class="ml-2">{{
              isExpanded ? 'Inklappen' : 'Meer tonen'
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </BaseCard>
</template>
